import { useState, useRef } from 'react'
import { Link, router, usePage } from '@inertiajs/react'
import { X } from 'lucide-react'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Badge } from '@/shared/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table'
import { t } from '@/shared/lib/i18n'
import {
  adminInstrumentTagPath,
  adminInstrumentTagsPath,
  editAdminInstrumentTagPath,
  newAdminInstrumentTagPath,
  restoreAdminInstrumentTagPath,
} from '@/shared/lib/routes'
import { useBreadcrumbs } from '@/shared/lib/breadcrumbs-context'
import { useCan } from '@/shared/hooks/useCan'
import { useDebouncedCallback } from '@/shared/hooks/useDebouncedCallback'
import { Resources, Actions } from '@/shared/rbac'
import type { WebAdminInstrumentTag } from '@/shared/types/web'
import type { PaginationMeta } from '@/shared/types/PaginationMeta'

interface Props {
  tags: WebAdminInstrumentTag[]
  pagination: PaginationMeta
  filters: { search: string; state: string }
  [key: string]: unknown
}

const InstrumentTagsIndexPage = () => {
  const { tags, pagination, filters } = usePage<Props>().props

  useBreadcrumbs([{ label: t('nav.administration') }, { label: t('nav.instrumentTags') }])

  const [search, setSearch] = useState(filters.search)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [state, setState] = useState(filters.state || 'all')

  const canWrite = useCan(Resources.ADMIN_INSTRUMENT_TAG, Actions.CREATE)
  const canDelete = useCan(Resources.ADMIN_INSTRUMENT_TAG, Actions.DELETE)

  const navigate = (overrides: { search?: string; state?: string } = {}) => {
    const effectiveSearch = overrides.search ?? search
    const effectiveState = overrides.state ?? state
    const params: Record<string, string> = {}
    if (effectiveSearch) params.search = effectiveSearch
    if (effectiveState && effectiveState !== 'all') params.state = effectiveState
    router.visit(adminInstrumentTagsPath(Object.keys(params).length ? params : {}), {
      preserveState: false,
    })
  }

  const debouncedNavigate = useDebouncedCallback((value: string) => {
    navigate({ search: value })
  }, 400)

  const activeFilterCount = [search, state !== 'all'].filter(Boolean).length

  const clearFilters = () => {
    debouncedNavigate.cancel()
    setSearch('')
    setState('all')
    router.visit(adminInstrumentTagsPath(), { preserveState: false })
  }

  const handleDeactivate = (id: number) => {
    if (!window.confirm(t('admin.instrumentTags.deactivateConfirm'))) return
    router.delete(adminInstrumentTagPath(id), { preserveScroll: true })
  }

  const handleActivate = (id: number) => {
    if (!window.confirm(t('admin.instrumentTags.activateConfirm'))) return
    router.patch(restoreAdminInstrumentTagPath(id), {}, { preserveScroll: true })
  }

  const goToPage = (page: number) => {
    const params = new URLSearchParams(window.location.search)
    params.set('page', String(page))
    router.visit(`${adminInstrumentTagsPath()}?${params}`, { preserveState: true })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('admin.instrumentTags.pageTitle')}</h1>
        {canWrite && (
          <Button asChild>
            <Link href={newAdminInstrumentTagPath()}>{t('admin.instrumentTags.newButton')}</Link>
          </Button>
        )}
      </div>

      <div className="space-y-3 rounded-md border p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative w-72">
            <Input
              ref={searchInputRef}
              placeholder={t('admin.instrumentTags.searchPlaceholder')}
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                debouncedNavigate(e.target.value)
              }}
              className={search ? 'pr-8' : ''}
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch('')
                  navigate({ search: '' })
                  searchInputRef.current?.focus()
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          <Select
            value={state}
            onValueChange={(value) => {
              setState(value)
              navigate({ state: value })
            }}
          >
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t('admin.instrumentTags.stateFilter.all')}</SelectItem>
              <SelectItem value="active">{t('admin.instrumentTags.stateFilter.active')}</SelectItem>
              <SelectItem value="inactive">
                {t('admin.instrumentTags.stateFilter.inactive')}
              </SelectItem>
            </SelectContent>
          </Select>
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="ml-auto text-muted-foreground"
              onClick={clearFilters}
            >
              {t('admin.instrumentTags.clearFilters')}
            </Button>
          )}
        </div>
      </div>

      {/* Mobile: card list */}
      <div className="space-y-2 lg:hidden">
        {tags.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-16 text-center">
            <p className="text-sm text-muted-foreground">{t('admin.instrumentTags.emptyState')}</p>
          </div>
        ) : (
          tags.map((tag) => (
            <div key={tag.id} className="rounded-lg border bg-card p-3 shadow-sm space-y-2">
              <p className="font-semibold">{tag.name}</p>
              <p className="text-sm text-muted-foreground">
                {t('admin.instrumentTags.instrumentsColumn')}: {tag.instrumentCount}
              </p>
              <div className="flex items-center justify-between gap-2">
                {tag.active ? (
                  <Badge variant="outline" className="text-green-700 border-green-300">
                    {t('admin.instrumentTags.activeLabel')}
                  </Badge>
                ) : (
                  <Badge variant="outline" className="text-red-700 border-red-300">
                    {t('admin.instrumentTags.inactiveLabel')}
                  </Badge>
                )}
                <div className="flex gap-2">
                  {canDelete &&
                    (tag.active ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-24"
                        onClick={() => handleDeactivate(tag.id)}
                      >
                        {t('admin.instrumentTags.deactivateButton')}
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-24"
                        onClick={() => handleActivate(tag.id)}
                      >
                        {t('admin.instrumentTags.activateButton')}
                      </Button>
                    ))}
                  {canWrite && tag.active && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => router.visit(editAdminInstrumentTagPath(tag.id))}
                    >
                      {t('admin.instrumentTags.editButton')}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Desktop: table */}
      <div className="hidden rounded-md border lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('admin.instrumentTags.nameField')}</TableHead>
              <TableHead>{t('admin.instrumentTags.instrumentsColumn')}</TableHead>
              <TableHead>{t('admin.instrumentTags.activeColumn')}</TableHead>
              {(canDelete || canWrite) && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {tags.map((tag) => (
              <TableRow key={tag.id}>
                <TableCell className="font-medium">{tag.name}</TableCell>
                <TableCell>{tag.instrumentCount}</TableCell>
                <TableCell>
                  {tag.active ? (
                    <Badge variant="outline" className="text-green-700 border-green-300">
                      {t('admin.instrumentTags.activeLabel')}
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-red-700 border-red-300">
                      {t('admin.instrumentTags.inactiveLabel')}
                    </Badge>
                  )}
                </TableCell>
                {(canDelete || canWrite) && (
                  <TableCell>
                    <div className="flex gap-2 justify-end">
                      {canDelete &&
                        (tag.active ? (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-24"
                            onClick={() => handleDeactivate(tag.id)}
                          >
                            {t('admin.instrumentTags.deactivateButton')}
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            className="w-24"
                            onClick={() => handleActivate(tag.id)}
                          >
                            {t('admin.instrumentTags.activateButton')}
                          </Button>
                        ))}
                      {canWrite && tag.active && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => router.visit(editAdminInstrumentTagPath(tag.id))}
                        >
                          {t('admin.instrumentTags.editButton')}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                )}
              </TableRow>
            ))}
            {tags.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={canDelete || canWrite ? 4 : 3}
                  className="text-center text-muted-foreground py-8"
                >
                  {t('admin.instrumentTags.emptyState')}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>
          {t('admin.instrumentTags.paginationPage')} {pagination.page}{' '}
          {t('admin.instrumentTags.paginationOf')} {pagination.totalPages} ({pagination.total}{' '}
          {t('admin.instrumentTags.paginationNoun')})
        </span>
        {pagination.totalPages > 1 && (
          <div className="flex gap-2">
            {pagination.page > 1 && (
              <Button variant="outline" size="sm" onClick={() => goToPage(pagination.page - 1)}>
                {t('admin.instrumentTags.previous')}
              </Button>
            )}
            {pagination.page < pagination.totalPages && (
              <Button variant="outline" size="sm" onClick={() => goToPage(pagination.page + 1)}>
                {t('admin.instrumentTags.next')}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default InstrumentTagsIndexPage
